import { useEffect, useState } from 'react';

export type RulesTocItem = {
  id: string;
  label: string;
};

type RulesTocProps = {
  items: RulesTocItem[];
};

export default function RulesToc({ items }: RulesTocProps) {
  const [activeId, setActiveId] = useState(items[0]?.id ?? '');

  useEffect(() => {
    const sections = items
      .map(({ id }) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);

    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        root: document.getElementById('main'),
        rootMargin: '-10% 0px -55% 0px',
        threshold: [0.1, 0.35, 0.6],
      }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [items]);

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);
    if (!section) return;
    setActiveId(id);
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', `#${id}`);
  };

  return (
    <nav
      aria-label="Rules sections"
      className="sticky top-0 z-20 -mx-4 mb-8 border-b border-bk-gold-dim/30 bg-bk-bg/95 px-4 py-3 backdrop-blur-sm sm:-mx-6 sm:px-6 lg:-mx-10 lg:px-10"
    >
      <ul className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:flex-wrap md:overflow-visible md:pb-0">
        {items.map(({ id, label }) => {
          const active = activeId === id;
          return (
            <li key={id} className="shrink-0">
              <a
                href={`#${id}`}
                onClick={(event) => {
                  event.preventDefault();
                  scrollToSection(id);
                }}
                className={[
                  'inline-flex items-center rounded-sm border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors sm:text-sm',
                  active
                    ? 'border-bk-gold bg-bk-gold/15 text-bk-gold-bright'
                    : 'border-bk-border text-bk-muted hover:border-bk-gold-dim hover:text-bk-gold',
                ].join(' ')}
              >
                {label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
